// Atlas Dashboard — Settings Page
// All configuration: tracking, notifications, Gmail, data, reports.

import { useState } from "react";
import { Settings as SettingsIcon, Mail, Bell, Database, FileText, Shield, Save, CheckCircle } from "lucide-react";
import { useSettings } from "../hooks/useApi";
import { api } from "../lib/api-client";
import type { AtlasSettings } from "@atlas/shared";

export function SettingsPage() {
  const { data: settings, loading, error, refetch } = useSettings();
  const [activeTab, setActiveTab] = useState("tracking");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [localSettings, setLocalSettings] = useState<Partial<AtlasSettings>>({});

  if (loading) return <div style={{ padding: "48px", textAlign: "center", color: "var(--color-text-tertiary)" }}>Loading settings...</div>;
  if (error) return <div style={{ padding: "48px", textAlign: "center", color: "#DC2626" }}>{error}</div>;
  if (!settings) return null;

  const currentSettings = { ...settings, ...localSettings };

  async function saveSettings(updates: Partial<AtlasSettings>) {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/api/settings", updates);
      setLocalSettings((prev) => ({ ...prev, ...updates }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Could not save settings.");
    }
    setSaving(false);
  }

  const tabs = [
    { key: "tracking", label: "Tracking", icon: Mail },
    { key: "gmail", label: "Gmail", icon: Shield },
    { key: "data", label: "Data", icon: Database },
  ];

  return (
    <div style={{ display: "flex", gap: "24px" }}>
      {/* Sub-navigation */}
      <div style={{ width: "200px", flexShrink: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  border: "none",
                  background: activeTab === tab.key ? "#EFF6FF" : "transparent",
                  color: activeTab === tab.key ? "#2563EB" : "var(--color-text-secondary)",
                  fontSize: "14px",
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  cursor: "pointer",
                  textAlign: "left" as const,
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Settings panel */}
      <div style={{ flex: 1 }}>
        {activeTab === "tracking" && (
          <SettingsSection title="Tracking Preferences">
            <ToggleRow label="Open tracking" description="Track when emails are opened" value={currentSettings.openTrackingEnabled!} onChange={(v) => saveSettings({ openTrackingEnabled: v })} />
            <ToggleRow label="Link tracking" description="Track when links are clicked" value={currentSettings.linkTrackingEnabled!} onChange={(v) => saveSettings({ linkTrackingEnabled: v })} />
            <ToggleRow label="Self-open suppression" description="Don't count your own opens of your sent emails" value={currentSettings.selfOpenSuppression!} onChange={(v) => saveSettings({ selfOpenSuppression: v })} />
          </SettingsSection>
        )}



        {activeTab === "gmail" && (
          <SettingsSection title="Gmail Connection">
            <div style={{ padding: "16px", backgroundColor: "#F0FDF4", borderRadius: "8px", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <CheckCircle size={16} color="#16A34A" />
                <span style={{ fontSize: "14px", fontWeight: 600, color: "#16A34A" }}>Gmail is connected</span>
              </div>
              <div style={{ fontSize: "13px", color: "var(--color-text-secondary)" }}>
                Connected as: {settings.founderEmail || "Unknown"}
              </div>
            </div>
            <div style={{ fontSize: "13px", color: "var(--color-text-secondary)", marginBottom: "16px", lineHeight: 1.6 }}>
              <strong>What Atlas can do:</strong> Read your mailbox (to detect replies and build contact intelligence); Send emails as you (for campaigns and scheduled sends).
              <br /><br />
              <strong>What Atlas cannot do:</strong> Delete your emails, access your contacts list, see your password, or share your data.
            </div>
            <button
              onClick={() => {
                if (confirm("Disconnect Gmail? Atlas will stop tracking and detecting replies. You can reconnect at any time.")) {
                  api.post("/auth/disconnect").then(() => window.location.href = "/login");
                }
              }}
              style={{ padding: "10px 20px", backgroundColor: "transparent", color: "#DC2626", border: "1px solid #FECACA", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}
            >
              Disconnect Gmail
            </button>
          </SettingsSection>
        )}

        {activeTab === "data" && (
          <SettingsSection title="Data Retention & Export">
            <div style={{ marginBottom: "24px" }}>
              <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}>Retention Periods</div>
              <SelectRow label="Tracking events" description="How long to keep open/click events" value={String(currentSettings.retentionTrackingEventsDays!)} options={[{ value: "90", label: "3 months" }, { value: "180", label: "6 months" }, { value: "365", label: "1 year" }, { value: "730", label: "2 years" }, { value: "0", label: "Forever" }]} onChange={(v) => saveSettings({ retentionTrackingEventsDays: parseInt(v) })} />
              <SelectRow label="Stored files" description="How long to keep PDFs and attachments" value={String(currentSettings.retentionFilesDays!)} options={[{ value: "30", label: "1 month" }, { value: "90", label: "3 months" }, { value: "180", label: "6 months" }, { value: "365", label: "1 year" }]} onChange={(v) => saveSettings({ retentionFilesDays: parseInt(v) })} />
            </div>
          </SettingsSection>
        )}



        {/* Save indicator */}
        {saving && <div style={{ fontSize: "13px", color: "var(--color-text-tertiary)", marginTop: "16px" }}>Saving...</div>}
        {saved && <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#16A34A", marginTop: "16px" }}><CheckCircle size={14} /> Settings saved</div>}
      </div>
    </div>
  );
}

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", padding: "24px" }}>
      <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "20px" }}>{title}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>{children}</div>
    </div>
  );
}

function ToggleRow({ label, description, value, onChange }: { label: string; description: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #F3F4F6" }}>
      <div>
        <div style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-primary)" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "var(--color-text-tertiary)" }}>{description}</div>
      </div>
      <button onClick={() => onChange(!value)} style={{ position: "relative", width: "44px", height: "24px", borderRadius: "12px", border: "none", backgroundColor: value ? "#2563EB" : "#D1D5DB", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: "2px", left: value ? "22px" : "2px", width: "20px", height: "20px", backgroundColor: "white", borderRadius: "50%", transition: "left 0.2s" }} />
      </button>
    </div>
  );
}

function SelectRow({ label, description, value, options, onChange }: { label: string; description: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #F3F4F6" }}>
      <div>
        <div style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-primary)" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "var(--color-text-tertiary)" }}>{description}</div>
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: "6px", fontSize: "14px", backgroundColor: "var(--color-card)", color: "var(--color-text-primary)", cursor: "pointer", outline: "none" }}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}
